"use client";

import { createContext, useContext, type ReactNode } from "react";
import Link from "next/link";
import { Alert, AlertDescription, AlertTitle } from "@/components/ui/alert";
import { useNamespaceProviderIssue } from "@/hooks/useNamespaceProviderIssue";

const NamespaceWarningContext = createContext<{ namespaceId: string; slug: string } | null>(null);

/** The server resolves this from the route; ambient namespace selection is never consulted. */
export function NamespaceWarningScope({ namespaceId, slug, children }: { namespaceId: string; slug: string; children: ReactNode }) {
  return <NamespaceWarningContext.Provider value={{ namespaceId, slug }}>{children}</NamespaceWarningContext.Provider>;
}

export function NamespaceProviderWarning() {
  const namespace = useContext(NamespaceWarningContext);
  return namespace ? <ScopedProviderWarning namespace={namespace} /> : null;
}

function ScopedProviderWarning({ namespace }: { namespace: { namespaceId: string; slug: string } }) {
  const query = useNamespaceProviderIssue(namespace.namespaceId);
  const issue = query.data?.issue;
  if (!issue) {
    return query.isError ? <p role="status" className="text-sm text-muted-foreground">Provider warning status unavailable.</p> : null;
  }
  return (
    <Alert variant="warning" className="my-[var(--space-sm)] shrink-0">
      <AlertTitle>{issue.message.split(". ")[0]}</AlertTitle>
      <AlertDescription>
        {issue.message}{" "}
        <span>Last observed during an audit; this is not a live balance check.</span>{" "}
        <Link href={`/${encodeURIComponent(namespace.slug)}/audit`} className="underline">View audit</Link>
        {query.isError ? <span> Unable to refresh; showing the last recorded warning.</span> : null}
      </AlertDescription>
    </Alert>
  );
}
